import React, { useState, useMemo } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import {
  Box,
  Typography,
  Paper,
  Container,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TablePagination,
  TableSortLabel,
  Collapse,
  IconButton,
  TextField,
  Button,
  InputAdornment,
  Stack,
  CircularProgress,
  Chip,
  Grid,
  Card,
  CardContent,
  Switch,
  Tooltip,
  Menu,
  MenuItem,
  Dialog,
  DialogTitle,
  DialogContent
} from "@mui/material";
import { AiOutlineSearch, AiOutlineInfoCircle } from "react-icons/ai";
import { MdKeyboardArrowDown, MdKeyboardArrowUp, MdArrowBack } from "react-icons/md";
import { useQuery } from "@tanstack/react-query";
import { fetchCircuitReport } from "../../api/dashboardApi";
import { PiFileCsv } from "react-icons/pi";
import dayjs from "dayjs";
import IdleCircuitsOverview from "./IdleCircuitsOverview";
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  BarElement,
  PointElement,
  LineElement,
  Title,
  Tooltip as ChartTooltip,
  Legend,
  ArcElement,
  Filler
} from 'chart.js';
import { Bar, Pie, Line, Doughnut } from 'react-chartjs-2';

ChartJS.register(
  CategoryScale,
  LinearScale,
  BarElement,
  PointElement,
  LineElement,
  Title,
  ChartTooltip,
  Legend,
  ArcElement,
  Filler
);

/**
 * Simple tooltip descriptions for Circuit Report columns.
 */
const CIRCUIT_REPORT_TOOLTIPS = {
  "S.No.": "Row number in the report.",
  "Office": "Main office location — usually the city name.",
  "PIP Circuit ID": "Unique ID for the circuit.",
  "Service ID": "Unique ID for the service on this circuit.",
  "Service": "Name of the service (e.g., Internet, MPLS).",
  "MRC": "Monthly Recurring Charge — the standard monthly fee from the latest invoice.",
  "Contract Amount": "Agreed monthly amount as per the contract.",
  "Is Removed": "Whether this service has been disconnected — Yes or No.",
  "Months": "Number of months of billing data available.",
  "Month": "The billing month.",
  "Amount": "Total amount billed for this service during that month.",
  "Contract Amount Monthly": "Contract value tied to invoices in that month.",
  "Charge Type": "Type of charge that month (e.g., recurring, adjustment, setup fee).",
  "Invoice ID": "Invoice number(s) containing charges for that month.",
};

const HeaderWithTooltip = ({ label, tooltipKey }) => {
  const tip = CIRCUIT_REPORT_TOOLTIPS[tooltipKey || label];
  if (!tip) return label;
  return (
    <Tooltip
      title={
        <Typography variant="caption" sx={{ lineHeight: 1.3 }}>
          {tip}
        </Typography>
      }
      arrow
      placement="top"
    >
      <span style={{ cursor: "help" }}>
        {label}
      </span>
    </Tooltip>
  );
};

const LineItemsTable = ({ lineItems }) => {
  if (!lineItems || lineItems.length === 0) return <Typography variant="body2" color="textSecondary">No line items available.</Typography>;
  return (
    <TableContainer component={Paper} elevation={0} sx={{ border: "1px solid #e2e8f0", borderRadius: 1, my: 1, overflowX: 'auto' }}>
      <Table size="small" sx={{ minWidth: 600 }}>
        <TableHead>
          <TableRow>
            <TableCell sx={{ fontWeight: 700, fontSize: '11px', color: '#475569', textTransform: 'uppercase', bgcolor: '#e2e8f0' }}>Service</TableCell>
            <TableCell sx={{ fontWeight: 700, fontSize: '11px', color: '#475569', textTransform: 'uppercase', bgcolor: '#e2e8f0' }}>Description</TableCell>
            <TableCell sx={{ fontWeight: 700, fontSize: '11px', color: '#475569', textTransform: 'uppercase', bgcolor: '#e2e8f0' }}>Charge Type</TableCell>
            <TableCell sx={{ fontWeight: 700, fontSize: '11px', color: '#475569', textTransform: 'uppercase', bgcolor: '#e2e8f0' }}>Local Amount</TableCell>
            <TableCell sx={{ fontWeight: 700, fontSize: '11px', color: '#475569', textTransform: 'uppercase', bgcolor: '#e2e8f0' }}>Currency</TableCell>
            <TableCell sx={{ fontWeight: 700, fontSize: '11px', color: '#475569', textTransform: 'uppercase', bgcolor: '#e2e8f0' }}>Conv. Rate</TableCell>
            <TableCell sx={{ fontWeight: 700, fontSize: '11px', color: '#475569', textTransform: 'uppercase', bgcolor: '#e2e8f0' }}>USD Amount</TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {lineItems.map((item, idx) => (
            <TableRow key={idx} sx={{ '&:last-child td': { borderBottom: 0 } }}>
              <TableCell sx={{ fontSize: '12px', color: '#334155', whiteSpace: 'nowrap' }}>{item.service_name || '-'}</TableCell>
              <TableCell sx={{ fontSize: '11px', color: '#64748b', maxWidth: 250 }}>
                {item.service_description && item.service_description.length > 60 ? (
                  <Tooltip title={<span style={{ whiteSpace: 'pre-wrap' }}>{item.service_description}</span>} arrow placement="top">
                    <span style={{ cursor: "pointer", display: "-webkit-box", WebkitLineClamp: 2, WebkitBoxOrient: "vertical", overflow: "hidden", textOverflow: "ellipsis" }}>
                      {item.service_description}
                    </span>
                  </Tooltip>
                ) : (
                  <span style={{ whiteSpace: 'pre-wrap' }}>{item.service_description || '-'}</span>
                )}
              </TableCell>
              <TableCell sx={{ fontSize: '12px', color: '#334155', whiteSpace: 'nowrap' }}>{item.charge_type || '-'}</TableCell>
              <TableCell sx={{ fontSize: '12px', color: '#334155', whiteSpace: 'nowrap' }}>
                {item.amount !== undefined ? Number(item.amount).toFixed(2) : '-'}
              </TableCell>
              <TableCell sx={{ fontSize: '12px', color: '#334155', whiteSpace: 'nowrap' }}>{item.currency || '-'}</TableCell>
              <TableCell sx={{ fontSize: '12px', color: '#64748b', whiteSpace: 'nowrap' }}>
                {item.conversion_rate !== undefined ? Number(item.conversion_rate).toFixed(3) : '-'}
              </TableCell>
              <TableCell sx={{ fontSize: '12px', fontWeight: 600, whiteSpace: 'nowrap' }}>
                ${Number(item.amount_usd || 0).toFixed(2)}
              </TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>
    </TableContainer>
  );
};

const MonthRow = ({ m, contractViewTab, anchorEl, openMenu, handleMenuClick, handleMenuClose, handleOpenDialog, handleOpenWindow }) => {
  const [open, setOpen] = useState(false);
  const hasLineItems = m.line_items && m.line_items.length > 0;

  return (
    <React.Fragment>
      <TableRow sx={{ '& > *': { borderBottom: hasLineItems ? 'unset' : undefined } }}>
        <TableCell sx={{ width: 40, p: 0.5 }}>
          {hasLineItems && (
            <IconButton size="small" onClick={() => setOpen(!open)}>
              {open ? <MdKeyboardArrowUp /> : <MdKeyboardArrowDown />}
            </IconButton>
          )}
        </TableCell>
        <TableCell sx={{ fontSize: "12px" }}>{m.month || "N/A"}</TableCell>
        <TableCell sx={{ fontSize: "12px", color: "text.secondary" }}>{m.invoice_date || "N/A"}</TableCell>
        <TableCell sx={{ fontSize: "12px", fontWeight: 600 }}>${Number(m.amount_usd || 0).toFixed(2)}</TableCell>
        <TableCell sx={{ fontSize: "12px", fontWeight: 600 }}>{m.net_amount_usd !== undefined && m.net_amount_usd !== '' ? `$${Number(m.net_amount_usd || 0).toFixed(2)}` : "N/A"}</TableCell>
        <TableCell sx={{ fontSize: "12px", fontWeight: 600 }}>
          {contractViewTab === 'grouped' 
            ? (m.grouped_contract_amount !== undefined && m.grouped_contract_amount !== '' ? `$${Number(m.grouped_contract_amount || 0).toFixed(2)}` : "N/A")
            : (m.contract_amount !== undefined && m.contract_amount !== '' ? `$${Number(m.contract_amount || 0).toFixed(2)}` : "N/A")
          }
        </TableCell>
        <TableCell sx={{ fontSize: "12px" }}>{m.charge_type || "N/A"}</TableCell>
        <TableCell sx={{ fontSize: "12px", color: "text.secondary" }}>{m.invoice_id || "N/A"}</TableCell>
        <TableCell>
          {m.invoice_pdf_url ? (
            <>
              <Button
                variant="text"
                size="small"
                onClick={(e) => handleMenuClick(e, m.invoice_pdf_url)}
                sx={{ textTransform: 'none', color: '#0ea5e9', fontSize: '12px', fontWeight: 600, minWidth: 0, p: 0 }}
              >
                View PDF
              </Button>
              <Menu
                anchorEl={anchorEl}
                open={openMenu}
                onClose={handleMenuClose}
              >
                <MenuItem onClick={handleOpenDialog} sx={{ fontSize: '13px' }}>Open in Dialog</MenuItem>
                <MenuItem onClick={handleOpenWindow} sx={{ fontSize: '13px' }}>Open in New Window</MenuItem>
              </Menu>
            </>
          ) : '-'}
        </TableCell>
      </TableRow>
      {hasLineItems && (
        <TableRow>
          <TableCell style={{ paddingBottom: 0, paddingTop: 0 }} colSpan={9}>
            <Collapse in={open} timeout="auto" unmountOnExit>
              <Box sx={{ margin: 1, ml: 4, p: 1, backgroundColor: '#f8fafc', borderRadius: 1 }}>
                <Typography variant="subtitle2" sx={{ fontWeight: 600, color: '#475569', mb: 0.5 }}>Invoice Line Items</Typography>
                <LineItemsTable lineItems={m.line_items} />
              </Box>
            </Collapse>
          </TableCell>
        </TableRow>
      )}
    </React.Fragment>
  );
};

const CircuitRow = React.memo(({ row, onOpenPdf }) => {
  const [open, setOpen] = useState(false);
  const [contractViewTab, setContractViewTab] = useState('grouped');
  const [anchorEl, setAnchorEl] = useState(null);
  const [menuUrl, setMenuUrl] = useState(null);
  const openMenu = Boolean(anchorEl);

  const handleMenuClick = (event, url) => {
    event.stopPropagation();
    setMenuUrl(url);
    setAnchorEl(event.currentTarget);
  };

  const handleMenuClose = (event) => {
    if (event && event.stopPropagation) event.stopPropagation();
    setAnchorEl(null);
  };

  const handleOpenDialog = (event) => {
    event.stopPropagation();
    handleMenuClose();
    if (menuUrl && onOpenPdf) onOpenPdf(menuUrl);
  };

  const handleOpenWindow = (event) => {
    event.stopPropagation();
    handleMenuClose();
    if (menuUrl) window.open(menuUrl, '_blank', 'width=1000,height=800,scrollbars=yes,resizable=yes');
  };

  const months = row.months || [];
  const hasMonths = months.length > 0;
  const hasMrcLineItems = row.mrc_line_items && row.mrc_line_items.length > 0;

  return (
    <React.Fragment>
      <TableRow sx={{ "& > *": { borderBottom: "unset" } }}>
        <TableCell sx={{ width: 50 }}>
          {hasMonths && (
            <IconButton
              aria-label="expand row"
              size="small"
              onClick={() => setOpen(!open)}
            >
              {open ? <MdKeyboardArrowUp /> : <MdKeyboardArrowDown />}
            </IconButton>
          )}
        </TableCell>
        <TableCell sx={{ fontSize: "13px", fontWeight: 600 }}>{row["PIP Circuit ID"] || "N/A"}</TableCell>
        <TableCell sx={{ fontSize: "13px" }}>{row["Office"] || "N/A"}</TableCell>
        <TableCell sx={{ fontSize: "13px" }}>{row["Country"] || "N/A"}</TableCell>
        <TableCell sx={{ fontSize: "13px" }}>{row["Service ID"] || "N/A"}</TableCell>
        <TableCell sx={{ fontSize: "13px" }}>{row["Service"] || "N/A"}</TableCell>
        <TableCell sx={{ fontSize: "13px", fontWeight: 600 }}>
          {hasMrcLineItems ? (
            <Tooltip
              title={
                <Box sx={{ p: 1, minWidth: 400 }}>
                  <Typography variant="subtitle2" sx={{ mb: 1, fontWeight: 700, borderBottom: '1px solid #e2e8f0', pb: 0.5, color: '#334155' }}>
                    Invoice Date: {row.mrc_line_items[0]?.invoice_date || 'N/A'} | Invoice ID: {row.mrc_line_items[0]?.invoice_id || 'N/A'} | Service ID: {row.mrc_line_items[0]?.service_id || 'N/A'}
                  </Typography>
                  <Typography variant="caption" sx={{ display: 'block', mb: 1, fontWeight: 600, color: '#64748b' }}>
                    MRC Line Items ({row.mrc_invoice_month || "Current"})
                  </Typography>
                  <LineItemsTable lineItems={row.mrc_line_items} />
                </Box>
              }
              arrow
              placement="top"
              componentsProps={{
                tooltip: {
                  sx: {
                    bgcolor: '#f1f5f9',
                    color: '#333',
                    border: '1px solid #cbd5e1',
                    boxShadow: '0 4px 20px rgba(0,0,0,0.15)',
                    maxWidth: 'none'
                  }
                }
              }}
            >
              <span style={{ cursor: 'help', borderBottom: '1px dashed #cbd5e1' }}>
                ${Number(row["MRC"] || 0).toFixed(2)}
              </span>
            </Tooltip>
          ) : (
            `$${Number(row["MRC"] || 0).toFixed(2)}`
          )}
        </TableCell>
        <TableCell sx={{ fontSize: "13px" }}>${Number(row["Contract Amount"] || 0).toFixed(2)}</TableCell>
        <TableCell>
          <Chip
            label={row["Is Removed"] === "Yes" ? "Removed" : "Active"}
            size="small"
            color={row["Is Removed"] === "Yes" ? "error" : "success"}
            sx={{ fontWeight: 600, fontSize: "11px", height: "24px" }}
          />
        </TableCell>
        <TableCell sx={{ fontSize: "13px" }}>
          {hasMonths ? `${months.length} Months` : "No Data"}
        </TableCell>
      </TableRow>
      
      {/* Expanded Details Row */}
      <TableRow>
        <TableCell style={{ paddingBottom: 0, paddingTop: 0 }} colSpan={10}>
          <Collapse in={open} timeout="auto" unmountOnExit>
            <Box sx={{ margin: 2, p: 2, backgroundColor: "#fff", borderRadius: 2, border: "1px solid #e0e0e0" }}>
              
              <Grid container spacing={3} sx={{ mb: 2 }}>
                <Grid item xs={12} md={6}>
                  <Typography variant="subtitle2" sx={{ fontWeight: 600, color: "primary.main", mb: 1 }}>Circuit Details</Typography>
                  <Box sx={{ display: "grid", gridTemplateColumns: "120px 1fr", gap: 1, fontSize: "13px" }}>
                    <Typography variant="body2" color="textSecondary">Location:</Typography>
                    <Typography variant="body2">{row["Location"] || "N/A"}</Typography>
                    <Typography variant="body2" color="textSecondary">Ref From:</Typography>
                    <Typography variant="body2">{row["Ref From"] || "N/A"}</Typography>
                    <Typography variant="body2" color="textSecondary">Ref Doc ID:</Typography>
                    <Typography variant="body2">{row["Ref Doc ID"] || "N/A"}</Typography>
                    <Typography variant="body2" color="textSecondary">Ref Ref ID:</Typography>
                    <Typography variant="body2">{row["Ref Ref ID"] || "N/A"}</Typography>
                  </Box>
                </Grid>
                <Grid item xs={12} md={6}>
                  <Typography variant="subtitle2" sx={{ fontWeight: 600, color: "primary.main", mb: 1 }}>Status Details</Typography>
                  <Box sx={{ display: "grid", gridTemplateColumns: "160px 1fr", gap: 1, fontSize: "13px" }}>
                    <Typography variant="body2" color="textSecondary">Queue Status:</Typography>
                    <Typography variant="body2">{row["Queqe Status/disconnected"] || "N/A"}</Typography>
                    <Typography variant="body2" color="textSecondary">Disconnect Req No:</Typography>
                    <Typography variant="body2">{row["Disconnect Request Number"] || "N/A"}</Typography>
                    <Typography variant="body2" color="textSecondary">Submission Date:</Typography>
                    <Typography variant="body2">{row["Submisssion Date"] || "N/A"}</Typography>
                    <Typography variant="body2" color="textSecondary">Removed Date:</Typography>
                    <Typography variant="body2">{row["Removed Date"] || "N/A"}</Typography>
                  </Box>
                </Grid>
              </Grid>

              <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 1 }}>
                <Typography variant="subtitle2" sx={{ fontWeight: 600, color: "primary.main" }}>Monthly Breakdown</Typography>
                <Box sx={{ display: 'flex', backgroundColor: '#f4f6f8', borderRadius: '20px', p: '2px' }}>
                  <Box 
                    onClick={(e) => { e.stopPropagation(); setContractViewTab('grouped'); }}
                    sx={{ 
                      px: 1.5, py: 0.5, fontSize: '11px', fontWeight: 600, cursor: 'pointer', borderRadius: '20px',
                      backgroundColor: contractViewTab === 'grouped' ? '#7C2EA0' : 'transparent',
                      color: contractViewTab === 'grouped' ? '#fff' : '#64748b',
                      transition: 'all 0.2s ease',
                      display: 'flex', alignItems: 'center', justifyContent: 'center'
                    }}
                  >
                    Grouped
                  </Box>
                  <Box 
                    onClick={(e) => { e.stopPropagation(); setContractViewTab('individual'); }}
                    sx={{ 
                      px: 1.5, py: 0.5, fontSize: '11px', fontWeight: 600, cursor: 'pointer', borderRadius: '20px',
                      backgroundColor: contractViewTab === 'individual' ? '#7C2EA0' : 'transparent',
                      color: contractViewTab === 'individual' ? '#fff' : '#64748b',
                      transition: 'all 0.2s ease',
                      display: 'flex', alignItems: 'center', justifyContent: 'center'
                    }}
                  >
                    Individual
                  </Box>
                </Box>
              </Box>
              <TableContainer component={Paper} elevation={0} sx={{ border: "1px solid #eee"}}>
                <Table size="small">
                  <TableHead>
                    <TableRow sx={{ backgroundColor: "#f5f5f5" }}>
                      <TableCell sx={{ width: 40 }} />
                      <TableCell sx={{ fontWeight: 600, fontSize: "12px" }}><HeaderWithTooltip label="Month" /></TableCell>
                      <TableCell sx={{ fontWeight: 600, fontSize: "12px" }}><HeaderWithTooltip label="Invoice Date" /></TableCell>
                      <TableCell sx={{ fontWeight: 600, fontSize: "12px" }}><HeaderWithTooltip label="Amount" /></TableCell>
                      <TableCell sx={{ fontWeight: 600, fontSize: "12px" }}><HeaderWithTooltip label="NET Amount" /></TableCell>
                      <TableCell sx={{ fontWeight: 600, fontSize: "12px" }}>
                        <HeaderWithTooltip label="Contract Amount" tooltipKey="Contract Amount Monthly" />
                      </TableCell>
                      <TableCell sx={{ fontWeight: 600, fontSize: "12px" }}><HeaderWithTooltip label="Charge Type" /></TableCell>
                      <TableCell sx={{ fontWeight: 600, fontSize: "12px" }}><HeaderWithTooltip label="Invoice ID" /></TableCell>
                      <TableCell sx={{ fontWeight: 600, fontSize: "12px" }}><HeaderWithTooltip label="PDF" /></TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {months.map((m, idx) => (
                      <MonthRow 
                        key={idx} 
                        m={m} 
                        contractViewTab={contractViewTab}
                        anchorEl={anchorEl}
                        openMenu={openMenu}
                        handleMenuClick={handleMenuClick}
                        handleMenuClose={handleMenuClose}
                        handleOpenDialog={handleOpenDialog}
                        handleOpenWindow={handleOpenWindow}
                      />
                    ))}
                    {months.length === 0 && (
                      <TableRow>
                        <TableCell colSpan={9} align="center" sx={{ py: 3, color: "text.secondary" }}>
                          No monthly data available.
                        </TableCell>
                      </TableRow>
                    )}
                  </TableBody>
                </Table>
              </TableContainer>
            </Box>
          </Collapse>
        </TableCell>
      </TableRow>
    </React.Fragment>
  );
});

const RemovedServicesDashboard = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const [searchQuery, setSearchQuery] = useState("");
  const [tableSearchQuery, setTableSearchQuery] = useState("");
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(15);
  const [order, setOrder] = useState("asc");
  const [orderBy, setOrderBy] = useState("S.No.");

  const handleRequestSort = (property) => {
    const isAsc = orderBy === property && order === "asc";
    setOrder(isAsc ? "desc" : "asc");
    setOrderBy(property);
  };

  const initialData = location.state?.initialData || null;
  const initialStartDate = location.state?.startDate || "2025-09-01";
  const initialEndDate = location.state?.endDate || "2026-03-01";

  const [startDate, setStartDate] = useState(initialStartDate);
  const [endDate, setEndDate] = useState(initialEndDate);
  const [tempStartDate, setTempStartDate] = useState(initialStartDate);
  const [tempEndDate, setTempEndDate] = useState(initialEndDate);
  const [showLocationChart, setShowLocationChart] = useState(true);
  const [downloadingCircuitCSV, setDownloadingCircuitCSV] = useState(false);
  const [downloadMenuAnchor, setDownloadMenuAnchor] = useState(null);
  const [pdfDialogOpen, setPdfDialogOpen] = useState(false);
  const [currentPdfUrl, setCurrentPdfUrl] = useState('');

  const handleOpenPdfDialog = (url) => {
    setCurrentPdfUrl(url);
    setPdfDialogOpen(true);
  };

  const handleClosePdfDialog = () => {
    setPdfDialogOpen(false);
  };

  const handleDownloadCircuitReportCSV = async () => {
    try {
      setDownloadingCircuitCSV(true);
      const response = await fetchCircuitReport({
        start_date: startDate,
        end_date: endDate,
        download_csv: true,
      });

      if (response instanceof Blob) {
        const url = URL.createObjectURL(response);
        const link = document.createElement("a");
        link.href = url;
        link.download = `Removed_services_detailed_${dayjs().format("YYYYMMDD")}.csv`;
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
      } else {
        const csvContent = typeof response === "string" ? response : JSON.stringify(response);
        const blob = new Blob([csvContent], { type: "text/csv;charset=utf-8;" });
        const url = URL.createObjectURL(blob);
        const link = document.createElement("a");
        link.href = url;
        link.download = `Removed_services_detailed_${dayjs().format("YYYYMMDD")}.csv`;
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
      }
    } catch (error) {
      console.error("Error downloading circuit report CSV:", error);
    } finally {
      setDownloadingCircuitCSV(false);
    }
  };

  const handleDownloadUI_CSV = () => {
    try {
      const headers = [
        "Circuit ID",
        "Office",
        "Country",
        "Service ID",
        "Service",
        "Billed Amount",
        "Contract Amt",
        "Status",
        "Months"
      ];
      const csvRows = [headers.join(",")];
      
      // We use sortedData so it matches exactly what the user filtered/sorted in the table
      sortedData.forEach((row) => {
        const values = [
          `"${row["PIP Circuit ID"] || "N/A"}"`,
          `"${row["Office"] || "N/A"}"`,
          `"${row["Country"] || "N/A"}"`,
          `"${row["Service ID"] || "N/A"}"`,
          `"${row["Service"] || "N/A"}"`,
          `"$${Number(row["MRC"] || 0).toFixed(2)}"`,
          `"$${Number(row["Contract Amount"] || 0).toFixed(2)}"`,
          row["Is Removed"] === "Yes" ? "Removed" : "Active",
          row.months ? row.months.length : 0
        ];
        csvRows.push(values.join(","));
      });
      
      const csvString = csvRows.join("\n");
      const blob = new Blob([csvString], { type: "text/csv;charset=utf-8;" });
      const url = URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.href = url;
      link.setAttribute("download", `Removed_services_normal_${dayjs().format("YYYYMMDD")}.csv`);
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
    } catch (error) {
      console.error("Error downloading UI CSV:", error);
    }
    setDownloadMenuAnchor(null);
  };

  const shouldApplyInitialData = initialData && startDate === initialStartDate && endDate === initialEndDate;

  const { data: response, isLoading } = useQuery({
    queryKey: ["circuitReportDashboard", startDate, endDate],
    queryFn: () => fetchCircuitReport({ start_date: startDate, end_date: endDate, download_csv: false }),
    initialData: shouldApplyInitialData ? { data: initialData } : undefined,
    staleTime: 60 * 60 * 1000, // 1 hour
    gcTime: 60 * 60 * 1000, // 1 hour
  });

  const rawData = response?.data || [];

  const filteredData = useMemo(() => {
    if (!searchQuery) return rawData;
    const searchWords = searchQuery.toLowerCase().split(/\s+/).filter(Boolean);
    if (searchWords.length === 0) return rawData;
    return rawData.filter((item) => {
      const searchableText = [
        item["PIP Circuit ID"],
        item["Office"],
        item["Location"],
        item["Service ID"],
        item["Service"]
      ].filter(Boolean).join(" ").toLowerCase();
      return searchWords.every(word => searchableText.includes(word));
    });
  }, [rawData, searchQuery]);

  const sortedData = useMemo(() => {
    // 1. Table Search Filter
    let items = filteredData;
    if (tableSearchQuery) {
      const searchWords = tableSearchQuery.toLowerCase().split(/\s+/).filter(Boolean);
      if (searchWords.length > 0) {
        items = items.filter((item) => {
          const searchableText = [
            item["PIP Circuit ID"],
            item["Office"],
            item["Location"],
            item["Service"],
            item["Service ID"]
          ].filter(Boolean).join(" ").toLowerCase();
          return searchWords.every(word => searchableText.includes(word));
        });
      }
    }
    
    // 2. Sorting
    let sortableItems = [...items];
    sortableItems.sort((a, b) => {
      let aValue = a[orderBy];
      let bValue = b[orderBy];
      
      if (orderBy === "MRC" || orderBy === "Contract Amount") {
        aValue = parseFloat(aValue) || 0;
        bValue = parseFloat(bValue) || 0;
      } else if (orderBy === "S.No.") {
        aValue = parseInt(aValue, 10) || 0;
        bValue = parseInt(bValue, 10) || 0;
      } else {
        aValue = (aValue || "").toString().toLowerCase();
        bValue = (bValue || "").toString().toLowerCase();
      }

      if (aValue < bValue) return order === "asc" ? -1 : 1;
      if (aValue > bValue) return order === "asc" ? 1 : -1;
      return 0;
    });
    return sortableItems;
  }, [filteredData, tableSearchQuery, order, orderBy]);

  const paginatedData = useMemo(() => {
    return sortedData.slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage);
  }, [sortedData, page, rowsPerPage]);

  const handleChangePage = (event, newPage) => setPage(newPage);
  const handleChangeRowsPerPage = (event) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  // Compute Data for Charts
  const chartData = useMemo(() => {
    if (!filteredData.length) return null;

    let activeCount = 0;
    let removedCount = 0;
    const serviceCounts = {};
    const officeMRC = {};
    const locationMRC = {};
    const monthlyAmounts = {};

    filteredData.forEach(d => {
      // 1. Status Distribution
      if (d["Is Removed"] === "Yes") removedCount++;
      else activeCount++;

      // 2. Service Type
      const s = d["Service"] || "Unknown";
      serviceCounts[s] = (serviceCounts[s] || 0) + 1;

      // 3. Top Offices
      const o = d["Office"] || "Unknown";
      const mrc = parseFloat(d["MRC"]) || 0;
      if (mrc > 0) officeMRC[o] = (officeMRC[o] || 0) + mrc;

      // Top Locations
      const l = d["Location"] || "Unknown";
      if (mrc > 0) locationMRC[l] = (locationMRC[l] || 0) + mrc;

      // 4. Monthly Trend
      if (d.months) {
        d.months.forEach(m => {
          const monthName = m.month;
          const amt = parseFloat(m.amount) || 0;
          if (monthName) {
            monthlyAmounts[monthName] = (monthlyAmounts[monthName] || 0) + amt;
          }
        });
      }
    });

    const sortedOffices = Object.entries(officeMRC).sort((a, b) => b[1] - a[1]).slice(0, 10);
    const sortedLocations = Object.entries(locationMRC).sort((a, b) => b[1] - a[1]).slice(0, 10);
    const sortedMonths = Object.keys(monthlyAmounts).sort((a, b) => new Date(a) - new Date(b));

    return {
      status: {
        labels: ['Active Services', 'Removed Services'],
        datasets: [{
          data: [activeCount, removedCount],
          backgroundColor: ['#2e7d32', '#d32f2f'],
        }]
      },
      services: {
        labels: Object.keys(serviceCounts),
        datasets: [{
          data: Object.values(serviceCounts),
          backgroundColor: ['#1976d2', '#9c27b0', '#ed6c02', '#0288d1', '#d32f2f', '#2e7d32'],
        }]
      },
      offices: {
        labels: sortedOffices.map(o => o[0]),
        datasets: [{
          label: 'Total MRC ($)',
          data: sortedOffices.map(o => o[1]),
          backgroundColor: '#3b82f6',
          borderRadius: 4,
        }]
      },
      locations: {
        labels: sortedLocations.map(l => l[0]),
        datasets: [{
          label: 'Total MRC ($)',
          data: sortedLocations.map(l => l[1]),
          backgroundColor: '#10b981',
          borderRadius: 4,
        }]
      },
      monthly: {
        labels: sortedMonths,
        datasets: [{
          label: 'Total Charges',
          data: sortedMonths.map(m => monthlyAmounts[m]),
          borderColor: '#1976d2',
          backgroundColor: 'rgba(25, 118, 210, 0.2)',
          fill: true,
          tension: 0.3,
          pointRadius: 2,
        }]
      }
    };
  }, [filteredData]);

  // Chart Options
  const commonOptions = {
    responsive: true,
    maintainAspectRatio: false,
    plugins: {
      legend: { position: 'bottom' },
      datalabels: { display: false }
    }
  };

  const currencyTooltip = {
    callbacks: {
      label: function(context) {
        let label = context.dataset.label || '';
        if (label) label += ': $';
        else label += '$';
        label += Number(context.raw).toFixed(2);
        return label;
      }
    }
  };

  const barTooltip = {
    callbacks: {
      title: function(tooltipItems) {
        const item = tooltipItems[0];
        // Ensure the full label is returned, not the truncated tick
        return item.chart.data.labels[item.dataIndex];
      },
      label: function(context) {
        let label = context.dataset.label || '';
        if (label) label += ': $';
        else label += '$';
        label += Number(context.raw).toFixed(2);
        return label;
      }
    }
  };

  const lineOptions = {
    ...commonOptions,
    plugins: { 
      ...commonOptions.plugins, 
      legend: { display: false }, 
      tooltip: currencyTooltip,
      datalabels: {
        display: true,
        align: 'top',
        font: { size: 10 },
        formatter: (value) => '$' + Number(value).toFixed(2)
      }
    },
    scales: {
      x: { grid: { display: false } },
      y: { 
        grid: { color: '#f5f5f5' },
        ticks: {
          callback: function(value) {
            return '$' + Number(value).toFixed(2);
          }
        }
      }
    }
  };

  const barOptions = {
    indexAxis: 'y',
    responsive: true,
    maintainAspectRatio: false,
    layout: {
      padding: { right: 20 }
    },
    plugins: { 
      legend: { display: false }, 
      tooltip: barTooltip,
      datalabels: {
        display: true,
        align: 'right',
        anchor: 'end',
        font: { size: 10, weight: 'bold' },
        color: '#64748b',
        formatter: (value) => '$' + Number(value).toFixed(2)
      }
    },
    scales: {
      x: { 
        grid: { color: '#f5f5f5' },
        grace: '10%',
        ticks: {
          callback: function(value) {
            return '$' + Number(value).toFixed(2);
          }
        }
      },
      y: { 
        grid: { display: false },
        ticks: {
          callback: function(value) {
            const label = this.getLabelForValue(value) || '';
            if (label.length > 25) {
              return label.substring(0, 25) + '...';
            }
            return label;
          }
        }
      }
    }
  };

  const donutOptions = {
    ...commonOptions,
    cutout: '60%',
  };

  const pieOptions = {
    ...commonOptions,
    plugins: {
      ...commonOptions.plugins,
      datalabels: {
        display: true,
        color: '#fff',
        font: { size: 12, weight: 'bold' },
        formatter: (value) => value
      }
    }
  };

  const headCells = [
    { id: 'PIP Circuit ID', label: 'Circuit ID', tooltipKey: 'PIP Circuit ID' },
    { id: 'Office', label: 'Office', tooltipKey: 'Office' },
    { id: 'Country', label: 'Country', tooltipKey: 'Country' },
    { id: 'Service ID', label: 'Service ID', tooltipKey: 'Service ID' },
    { id: 'Service', label: 'Service', tooltipKey: 'Service' },
    { id: 'MRC', label: 'Billed Amount', tooltipKey: 'MRC' },
    { id: 'Contract Amount', label: 'Contract Amt', tooltipKey: 'Contract Amount' },
    { id: 'Is Removed', label: 'Status', tooltipKey: 'Is Removed' }
  ];

  return (
    <Box sx={{ p: 2, bgcolor: "#f9f9f9", minHeight: "100vh" }}>
      {/* Standard Header matching Dashboard.jsx */}
      <Box sx={{ mb: 3 }}>
        <IdleCircuitsOverview />
      </Box>

      {/* Filtered Section Container */}
      <Box sx={{ bgcolor: "#ffffff", borderRadius: "12px", p: 2, boxShadow: "0px 4px 12px rgba(0,0,0,0.05)" }}>
        
        {/* Filter Header */}
        <Box
          component="header"
          sx={{
            mb: 3,
            display: "flex",
            flexDirection: { xs: "column", md: "row" },
            alignItems: { xs: "stretch", md: "center" },
            gap: 2,
            justifyContent: "flex-end",
          }}
        >
          <Box sx={{ display: 'flex', flexDirection: { xs: 'column', sm: 'row' }, gap: 2, alignItems: 'center' }}>
            <TextField
              size="small"
              placeholder="Search filters..."
              value={searchQuery}
              onChange={(e) => {
                setSearchQuery(e.target.value);
                setPage(0);
              }}
              sx={{ width: { xs: '100%', sm: 200 } }}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <AiOutlineSearch size={18} />
                  </InputAdornment>
                ),
              }}
            />
            <TextField
              label="Start Date"
              type="date"
              size="small"
              value={tempStartDate}
              onChange={(e) => setTempStartDate(e.target.value)}
              InputLabelProps={{ shrink: true }}
              sx={{ width: { xs: '100%', sm: 140 } }}
            />
            <TextField
              label="End Date"
              type="date"
              size="small"
              value={tempEndDate}
              onChange={(e) => setTempEndDate(e.target.value)}
              InputLabelProps={{ shrink: true }}
              sx={{ width: { xs: '100%', sm: 140 } }}
            />
            <Button 
              variant="contained" 
              color="primary"
              disabled={tempStartDate === startDate && tempEndDate === endDate}
              onClick={() => {
                setStartDate(tempStartDate);
                setEndDate(tempEndDate);
              }}
              sx={{ 
                height: 40,
                textTransform: 'none',
                boxShadow: '0 2px 4px rgba(0,0,0,0.1)'
              }}
            >
              Apply
            </Button>
          </Box>
        </Box>

      {isLoading ? (
        <Box sx={{ display: "flex", justifyContent: "center", alignItems: "center", p: 10 }}>
          <CircularProgress color="primary" />
        </Box>
      ) : chartData ? (
        <Grid container spacing={2} sx={{ mb: 3 }}>
          {/* First Row */}
          <Grid item xs={12} md={12}>
            <Card sx={{ height: "100%", borderRadius: 2, boxShadow: "0px 6px 20px rgba(0,0,0,0.1)" }}>
              <CardContent>
                <Typography variant="subtitle1" fontWeight={600} sx={{ mb: 2 }}>Monthly Charges Trend</Typography>
                <Box sx={{ height: 280 }}>
                  <Line data={chartData.monthly} options={lineOptions} />
                </Box>
              </CardContent>
            </Card>
          </Grid>

          {/* Second Row */}
          <Grid item xs={12} md={8}>
            <Card sx={{ height: "100%", borderRadius: 2, boxShadow: "0px 6px 20px rgba(0,0,0,0.1)" }}>
              <CardContent>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                  <Typography variant="subtitle1" fontWeight={600}>
                    {showLocationChart ? "Top 10 Locations by Total MRC" : "Top 10 Offices by Total MRC"}
                  </Typography>
                  {/* <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <Typography variant="body2" sx={{ color: showLocationChart ? "text.secondary" : "#3b82f6", fontWeight: showLocationChart ? 400 : 600 }}>Offices</Typography>
                    <Switch
                      checked={showLocationChart}
                      onChange={(e) => setShowLocationChart(e.target.checked)}
                      size="small"
                      sx={{
                        '& .MuiSwitch-switchBase.Mui-checked': {
                          color: '#10b981',
                        },
                        '& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track': {
                          backgroundColor: '#10b981',
                        },
                        '& .MuiSwitch-switchBase': {
                          color: '#3b82f6',
                        },
                        '& .MuiSwitch-switchBase + .MuiSwitch-track': {
                          backgroundColor: '#3b82f6',
                        },
                      }}
                    />
                    <Typography variant="body2" sx={{ color: showLocationChart ? "#10b981" : "text.secondary", fontWeight: showLocationChart ? 600 : 400 }}>Locations</Typography>
                  </Box> */}
                </Box>
                <Box sx={{ height: 320 }}>
                  <Bar data={showLocationChart ? chartData.locations : chartData.offices} options={barOptions} />
                </Box>
              </CardContent>
            </Card>
          </Grid>
          <Grid item xs={12} md={4}>
            <Card sx={{ height: "100%", borderRadius: 2, boxShadow: "0px 6px 20px rgba(0,0,0,0.1)" }}>
              <CardContent>
                <Typography variant="subtitle1" fontWeight={600} sx={{ mb: 2 }}>Services Breakdown</Typography>
                <Box sx={{ height: 320, display: 'flex', justifyContent: 'center' }}>
                  <Pie data={chartData.services} options={pieOptions} />
                </Box>
              </CardContent>
            </Card>
          </Grid>
        </Grid>
      ) : (
        <Card sx={{ p: 5, textAlign: "center", mb: 3 }}>
          <Typography color="textSecondary">No chart data available for the selected criteria.</Typography>
        </Card>
      )}

      {/* Render Table Below Charts */}
      {!isLoading && (
        <Card sx={{ borderRadius: 2, boxShadow: "0px 6px 20px rgba(0,0,0,0.1)" }}>
          <Box sx={{ p: 2, borderBottom: "1px solid #eee", display: "flex", flexDirection: { xs: 'column', sm: 'row' }, justifyContent: "space-between", alignItems: { xs: 'flex-start', sm: 'center' }, gap: 2 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <Typography variant="subtitle1" fontWeight={600}>Detailed Circuit Records</Typography>
              <Tooltip 
                title={
                  <Box sx={{ p: 0.5, display: 'flex', flexDirection: 'column', gap: 1 }}>
                    <Box>
                      <Typography variant="subtitle2" sx={{ fontWeight: 700 }}>Circuit Records</Typography>
                      <Typography variant="caption" sx={{ display: 'block', lineHeight: 1.2 }}>Shows all circuit records available in invoices.</Typography>
                    </Box>
                    <Box>
                      <Typography variant="subtitle2" sx={{ fontWeight: 700 }}>Monthly Invoice Data</Typography>
                      <Typography variant="caption" sx={{ display: 'block', lineHeight: 1.2 }}>Shows all invoice records for the selected Service ID within the chosen date range.</Typography>
                    </Box>
                  </Box>
                } 
                arrow 
                placement="right"
              >
                <span style={{ display: 'flex', alignItems: 'center', cursor: 'pointer' }}>
                  <AiOutlineInfoCircle size={16} />
                </span>
              </Tooltip>
            </Box>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <TextField
                size="small"
                placeholder="Search table records..."
                value={tableSearchQuery}
                onChange={(e) => {
                  setTableSearchQuery(e.target.value);
                  setPage(0);
                }}
                sx={{ width: { xs: '100%', sm: 250 } }}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <AiOutlineSearch size={18} />
                    </InputAdornment>
                  ),
                }}
              />
              <Tooltip title="Download CSV Options" placement="top" arrow>
                <span
                  onClick={(e) => setDownloadMenuAnchor(e.currentTarget)}
                  style={{
                    display: "inline-flex",
                    alignItems: "center",
                    justifyContent: "center",
                    borderRadius: "50%",
                    width: "36px",
                    height: "36px",
                    backgroundColor: "#f5f5f5",
                    cursor: downloadingCircuitCSV ? "default" : "pointer",
                    opacity: downloadingCircuitCSV ? 0.5 : 1,
                    transition: "all 0.2s ease",
                    marginLeft: "8px"
                  }}
                  onMouseEnter={(e) => {
                    if (!downloadingCircuitCSV) {
                      e.currentTarget.style.backgroundColor = "#e0e0e0";
                    }
                  }}
                  onMouseLeave={(e) => (e.currentTarget.style.backgroundColor = "#f5f5f5")}
                >
                  {downloadingCircuitCSV ? (
                    <CircularProgress size={20} color="inherit" />
                  ) : (
                    <PiFileCsv size={22} color="#333" />
                  )}
                </span>
              </Tooltip>
              <Menu
                anchorEl={downloadMenuAnchor}
                open={Boolean(downloadMenuAnchor)}
                onClose={() => setDownloadMenuAnchor(null)}
              >
                <MenuItem onClick={handleDownloadUI_CSV}>Normal CSV</MenuItem>
                <MenuItem onClick={() => { setDownloadMenuAnchor(null); handleDownloadCircuitReportCSV(); }}>Detailed CSV</MenuItem>
              </Menu>
            </Box>
          </Box>
          <TableContainer sx={{ maxHeight: "calc(100vh - 250px)" }}>
            <Table stickyHeader size="medium">
              <TableHead>
                <TableRow>
                  <TableCell sx={{ backgroundColor: "#fafafa", width: 50 }} />
                  {headCells.map((headCell) => (
                    <TableCell
                      key={headCell.id}
                      sx={{ backgroundColor: "#fafafa", fontWeight: 600 }}
                      sortDirection={orderBy === headCell.id ? order : false}
                    >
                      <TableSortLabel
                        active={orderBy === headCell.id}
                        direction={orderBy === headCell.id ? order : 'asc'}
                        onClick={() => handleRequestSort(headCell.id)}
                      >
                        <HeaderWithTooltip label={headCell.label} tooltipKey={headCell.tooltipKey} />
                      </TableSortLabel>
                    </TableCell>
                  ))}
                  <TableCell sx={{ backgroundColor: "#fafafa", fontWeight: 600 }}><HeaderWithTooltip label="Months" /></TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {paginatedData.length > 0 ? (
                  paginatedData.map((row, idx) => (
                    <CircuitRow key={`${row["S.No."]}-${idx}`} row={row} onOpenPdf={handleOpenPdfDialog} />
                  ))
                ) : (
                  <TableRow>
                    <TableCell colSpan={10} align="center" sx={{ py: 6, color: "text.secondary" }}>
                      No records found matching your criteria.
                    </TableCell>
                  </TableRow>
                )}
              </TableBody>
            </Table>
          </TableContainer>
          <TablePagination
            rowsPerPageOptions={[15, 25, 50, 100]}
            component="div"
            count={sortedData.length}
            rowsPerPage={rowsPerPage}
            page={page}
            onPageChange={handleChangePage}
            onRowsPerPageChange={handleChangeRowsPerPage}
            sx={{ borderTop: "1px solid #eee" }}
          />
        </Card>
      )}
      </Box>
      <Dialog open={pdfDialogOpen} onClose={handleClosePdfDialog} maxWidth="lg" fullWidth>
        <DialogTitle sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', p: 2 }}>
          <Typography variant="h6" fontSize="16px" fontWeight="bold">Invoice Preview</Typography>
          <Button onClick={handleClosePdfDialog} color="inherit" size="small">Close</Button>
        </DialogTitle>
        <DialogContent dividers sx={{ height: '80vh', p: 0 }}>
          {currentPdfUrl && (
            <iframe
              src={currentPdfUrl}
              title="PDF Preview"
              width="100%"
              height="100%"
              style={{ border: 'none' }}
            />
          )}
        </DialogContent>
      </Dialog>
    </Box>
  );
};

export default RemovedServicesDashboard;
